import { useRCAMainStyles } from "pages/Corporate/RCA/RCA.styles";
import { useStyles } from "./RCAActionPlanDetail.styles";
import MainTransparentBackButton from "components/MainTransparentBackButton";
import RCATicketNumber from "pages/Corporate/RCA/components/RCATicketNumber";
import { IRCATicketTypeEnum } from "pages/Corporate/RCA/components/RCATicketNumber/RCATicketNumberInterfaces";
import {
    Alert,
    Box,
    Breadcrumbs,
    Button,
    Card,
    CircularProgress,
    Divider,
    InputLabel,
    LinearProgress,
    Link,
    Snackbar,
    TextField,
    Typography,
} from "@mui/material";
import MainCardButtonAction from "components/MainCardButtonAction";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { DatePicker } from "@mui/x-date-pickers";
import UploadDropZone from "components/UploadDropZone/UploadDropZone.view";
import RCAUploadZone from "pages/Corporate/RCA/components/RCAUploadZone";
import { RCAUploadTypeEnum } from "pages/Corporate/RCA/components/RCAUploadZone/RCAUploadZoneInterfaces";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import { createRef, useCallback, useContext, useMemo, useState } from "react";
import { DropzoneRef } from "react-dropzone";
import {
    CORPORATE_BACKOFFICE_PATHS,
    RCA_PRIORITIES,
    RCA_STATUSES,
    documentsFileTypeExtensions,
    documentsFileTypeExtensionsRegExp,
    mediaFileTypeExtensions,
    mediaFileTypeExtensionsRegExp,
} from "constants/constants";
import { useTranslation } from "react-i18next";
import RCAStatusDropdown from "pages/Corporate/RCA/components/RCAStatusDropdown";
import { IRCAActionPlanDetailFormValues } from "./RCAActionPlanDetailInterfaces";
import { Form, Formik } from "formik";
import { addRCAActionPlanDetailFormValidationSchema } from "validations/schemas";
import dayjs from "dayjs";
import useRolesPermissions from "hooks/useRolesPermissions";
import RCAUsersDropdown from "pages/Corporate/RCA/components/RCAUsersDropdown";
import { ActionPlanEntity } from "entities/RCA/ActionPlanEntity/ActionPlanEntity";
import RCAPriorityDropdown from "pages/Corporate/RCA/components/RCAPriorityDropdown";
import { uploadActionPlanFiles } from "api/queries/RCA/ActionPlanAPI";
import RCAActionPlanTasks from "./components/RCAActionPlanTasks";
import { UserContext } from "contexts/UserContext/UserContext";
import useDateFormat from "hooks/useDateFormat";
import { DocumentPreviewer } from "components/DocumentPreviewer/DocumentPreviewer.view";
import { RCAElipsisMenu } from "pages/Corporate/RCA/components/RCAElipsisMenu/RCAElipsisMenu";
import { RCAMediaPreviewer } from "pages/Corporate/RCA/components/RCAMediaPreviewer/RCAMediaPreviewer";
import moment from "moment";
import RCAConfirmDeleteModal from "pages/Corporate/RCA/components/RCAConfirmDeleteModal/RCAConfirmDeleteModal";
import RCAEditActionPlanModal from "pages/Corporate/RCA/components/RCAEditActionPlanModal/RCAEditActionPlanModal";
import MainModal from "components/MainModal";
import { RCAActionPlanHistoryLogs } from "./components/RCAActionPlanHistoryLogs";
import RCAMeetingTypesDropdown from "pages/Corporate/RCA/components/RCAMeetingTypesDropdown/RCAMeetingTypesDropdown";
import ActionPlanComments from "./components/ActionPlanComments";

const RCAActionPlanDetail = () => {
    const classes = useStyles();
    const mainClasses = useRCAMainStyles();
    const { actionPlanId } = useParams();
    const dropzoneDocumentsRef = createRef<DropzoneRef>();
    const dropzoneMediaRef = createRef<DropzoneRef>();
    const navigate = useNavigate();
    const location = useLocation();
    const [isSaving, setIsSaving] = useState(false);
    const { t } = useTranslation();
    const { user_id } = useContext(UserContext);
    const { getDateFormatByLanguage } = useDateFormat();

    const { actionPlan, loading, refetch } = ActionPlanEntity.GetActionPlan(Number(actionPlanId)!);
    const {
        actionPlanLogs,
        loading: loadingHistoryLogs,
        refetch: refetchHistoryLogs,
    } = ActionPlanEntity.GetActionPlanHistoricChangesLog(Number(actionPlanId)!);

    const [progress, setProgress] = useState(0);
    const [isUploadingFiles, setIsUploadingFiles] = useState<RCAUploadTypeEnum | null>(null);
    const { updateActionPlanMutation } = ActionPlanEntity.UpdateActionPlan();
    const { updateActionPlanMediaMutation } = ActionPlanEntity.UpdateActionPlanMedia();
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [openActionPlanDeleteModal, setOpenActionPlanDeleteModal] = useState<boolean>(false);
    const [openEditActionPlanModalOpen, setOpenEditActionPlanModalOpen] = useState(false);
    const [snackbarFailureMessage, setSnackbarFailureMessage] = useState("");
    const [isFileUploadModalOpen, setIsFileUploadModalOpen] = useState(false);
    const [fileDescription, setFileDescription] = useState("");
    const [pendingFiles, setPendingFiles] = useState<File[]>([]);

    const rcaOwnershipIds = useMemo(
        () =>
            actionPlan
                ? [
                      ...(actionPlan?.submitted_by_user_id ? [actionPlan.submitted_by_user_id] : []),
                      ...(actionPlan?.assignee ? [actionPlan.assignee.user_id] : []),
                      ...(actionPlan?.approver_id ? [actionPlan.approver_id] : []),
                      ...(actionPlan?.created_by ? [actionPlan?.created_by] : []),
                  ]
                : [],
        [actionPlan],
    );
    const { RCA_PERMISSIONS } = useRolesPermissions(rcaOwnershipIds);

    const initFormValues: IRCAActionPlanDetailFormValues = {
        assigneeId: actionPlan?.assignee?.user_id || "",
        priorityId: actionPlan?.priority?.priority_id || RCA_PRIORITIES.LOW,
        dueDate: new Date(actionPlan?.due_date) || new Date(),
        statusId: actionPlan?.status?.status_id || RCA_STATUSES.NOT_STARTED,
        meetingTypeId:
            actionPlan?.meeting_type_id || actionPlan?.rca?.meeting_type_id || actionPlan?.issue?.meeting_type_id,
    };

    const actionPlanEditDetails = useMemo(
        () => ({ actionPlanId: actionPlan.action_plan_id, name: actionPlan.name, description: actionPlan.description }),
        [actionPlan],
    );

    const rcaElipsisMenuOptions = useMemo(
        () => [
            {
                name: t("rca.action_plans.action_plan_details.edit_action_plan"),
                callback: () => setOpenEditActionPlanModalOpen(true),
                disabled: !RCA_PERMISSIONS?.action_plans?.canEdit,
            },
            {
                name: t("rca.action_plans.action_plan_details.delete_action_plan"),
                callback: () => setOpenActionPlanDeleteModal(true),
                disabled: !RCA_PERMISSIONS?.action_plans?.canDelete,
            },
        ],
        [RCA_PERMISSIONS, t],
    );

    const documentFiles = useMemo(() => {
        if (!actionPlan?.action_plan_media) return [];
        return actionPlan.action_plan_media.filter((file) =>
            documentsFileTypeExtensionsRegExp.test(file.media.media_type),
        );
    }, [actionPlan]);

    const mediaFiles = useMemo(() => {
        if (!actionPlan?.action_plan_media) return [];
        return actionPlan.action_plan_media.filter((file) => mediaFileTypeExtensionsRegExp.test(file.media.media_type));
    }, [actionPlan]);

    const handleFileChange = async (files: File[], mediaType: RCAUploadTypeEnum) => {
        if (!files || (files && !files.length)) return;
        setIsUploadingFiles(mediaType);
        setPendingFiles(files);
        setIsFileUploadModalOpen(true);
    };

    const handleFileUpload = async () => {
        if (!pendingFiles.length) return;
        const fileExtension = pendingFiles[0].name.split(".").pop();
        const renamedFile = new File(
            [pendingFiles[0]],
            fileDescription ? `${fileDescription}.${fileExtension}` : pendingFiles[0].name,
            { type: pendingFiles[0].type },
        );
        const formData = new FormData();
        formData.set("file", renamedFile);
        formData.set("action_plan_id", actionPlanId!.toString() || "");
        formData.set("user_id", user_id!.toString() || "");

        try {
            await uploadActionPlanFiles(Number(actionPlanId), formData, (uploadEvt) => {
                let percentCompleted = Math.round((uploadEvt.loaded * 100) / (uploadEvt?.total || 1));
                setProgress(percentCompleted);
            });

            refetch();
            setProgress(0);
            setIsUploadingFiles(null);
            setSnackbarMessage(t("rca.issues.issue_detail.upload_success"));
        } catch (error) {
            setProgress(0);
            setIsUploadingFiles(null);
        }

        setFileDescription("");
        setIsFileUploadModalOpen(false);
        setPendingFiles([]);
    };

    const handleSubmit = async (values: IRCAActionPlanDetailFormValues) => {
        setIsSaving(true);
        const actionPlanDetailValues = {
            due_date: values.dueDate.toISOString(),
            status_id: values.statusId,
            assignee_id: values.assigneeId,
            priority_id: values.priorityId,
            ...(values.meetingTypeId && { meeting_type_id: values.meetingTypeId }),
        };
        try {
            await updateActionPlanMutation({
                variables: {
                    actionPlanId: actionPlan?.action_plan_id,
                    changes: {
                        ...actionPlanDetailValues,
                    },
                },
            });
            refetch();
            await refetchHistoryLogs();
            setIsSaving(false);
            setSnackbarMessage(t("rca.action_plans.action_plan_details.update_success"));
        } catch {
            setIsSaving(false);
            return;
        }
    };

    const handleDeleteActionPlan = async () => {
        const actionPlanDetailValues = {
            deleted_at: moment.utc().toDate(),
        };
        const state = location.state;

        try {
            await updateActionPlanMutation({
                variables: {
                    actionPlanId: actionPlan?.action_plan_id,
                    changes: {
                        ...actionPlanDetailValues,
                    },
                },
            });

            if (state?.from) {
                navigate(state?.from);
            } else {
                navigate(
                    `/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${CORPORATE_BACKOFFICE_PATHS.RCA.ROOT}/${CORPORATE_BACKOFFICE_PATHS.RCA.RCA_ACTION_PLANS.ROOT}`,
                );
            }
            setIsSaving(false);
        } catch {
            setSnackbarFailureMessage(t("rca.action_plans.action_plan_details.delete_failed"));
            setIsSaving(false);
            return;
        }
    };

    const handleDeleteMediaFile = async (actionPlanMediaId: number) => {
        const actionPlanMediaValues = {
            deleted_at: moment.utc().toDate(),
        };

        try {
            await updateActionPlanMediaMutation({
                variables: {
                    actionPlanMediaId: actionPlanMediaId,
                    changes: {
                        ...actionPlanMediaValues,
                    },
                },
            });
            await refetch();
            await refetchHistoryLogs();

            setSnackbarMessage(t("rca.rca_library.rca_detail.media.delete_success"));
        } catch {
            setSnackbarFailureMessage(t("rca.rca_library.rca_detail.media.delete_failed"));
            return;
        }
    };

    const handleActionPlanApprovalOrRejection = useCallback(
        async (setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void, approve: boolean) => {
            const updateValues = approve
                ? {
                      status_id: RCA_STATUSES.NOT_STARTED,
                      approved_on: new Date(),
                      approver_id: user_id,
                  }
                : {
                      status_id: RCA_STATUSES.REJECTED,
                      rejected_on: new Date(),
                      rejecter_id: user_id,
                  };
            try {
                await updateActionPlanMutation({
                    variables: {
                        actionPlanId: actionPlan?.action_plan_id,
                        changes: updateValues,
                    },
                });
                await refetch();
                await refetchHistoryLogs();
                setFieldValue("statusId", updateValues.status_id);
                setSnackbarMessage(
                    t(`rca.action_plans.action_plan_details.${approve ? "approve_success" : "reject_success"}`),
                );
            } catch {
                return;
            }
        },
        [actionPlan, refetch, t, updateActionPlanMutation, user_id, refetchHistoryLogs],
    );

    const approvalButtons = [{ approval: true }, { approval: false }];

    const parentTicketLink = useMemo(() => {
        if (actionPlan?.rca_id) {
            return (
                <Link
                    underline="hover"
                    color="inherit"
                    onClick={() =>
                        navigate(
                            `/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${CORPORATE_BACKOFFICE_PATHS.RCA.ROOT}/${CORPORATE_BACKOFFICE_PATHS.RCA.RCA_LIBRARY.ROOT}/${actionPlan?.rca_id}`,
                            {
                                state: {
                                    from: `/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${CORPORATE_BACKOFFICE_PATHS.RCA.ROOT}/${CORPORATE_BACKOFFICE_PATHS.RCA.RCA_ACTION_PLANS.ROOT}/${actionPlan.action_plan_id}`,
                                },
                            },
                        )
                    }
                    style={{ cursor: "pointer" }}
                >
                    <RCATicketNumber
                        ticketNumber={actionPlan.rca?.display_number || ""}
                        ticketType={IRCATicketTypeEnum.RCA}
                    />
                </Link>
            );
        } else if (actionPlan?.issue_id) {
            return (
                <Link
                    underline="hover"
                    color="inherit"
                    onClick={() =>
                        navigate(
                            `/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${CORPORATE_BACKOFFICE_PATHS.RCA.ROOT}/${CORPORATE_BACKOFFICE_PATHS.RCA.ISSUES.ROOT}/${actionPlan?.issue_id}`,
                            {
                                state: {
                                    from: `/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${CORPORATE_BACKOFFICE_PATHS.RCA.ROOT}/${CORPORATE_BACKOFFICE_PATHS.RCA.RCA_ACTION_PLANS.ROOT}/${actionPlan.action_plan_id}`,
                                },
                            },
                        )
                    }
                    style={{ cursor: "pointer" }}
                >
                    <RCATicketNumber
                        ticketNumber={actionPlan?.issue?.display_number || ""}
                        ticketType={IRCATicketTypeEnum.ISSUE}
                    />
                </Link>
            );
        }
    }, [actionPlan, navigate]);

    if ((!actionPlan && !loading) || (actionPlan && !actionPlan.action_plan_id))
        return (
            <div className={mainClasses.mainWrapper}>
                <div className={`${classes.issueDetailWrapper} ${classes.issueDetailWrapperNoData}`}>
                    <CircularProgress color="secondary" />
                    <span>{t("rca.action_plans.action_plan_details.loading")}</span>
                </div>
            </div>
        );

    if (!actionPlanId || !actionPlan)
        return (
            <div className={mainClasses.mainWrapper}>
                <div className={`${classes.issueDetailWrapper} ${classes.issueDetailWrapperNoData}`}>
                    <ErrorOutlineOutlinedIcon className={classes.noDataIcon} />
                    <span>{t("rca.action_plans.action_plan_details.no_data")}</span>
                </div>
            </div>
        );

    return (
        <div className={mainClasses.mainWrapper}>
            <div className={classes.issueDetailWrapper}>
                <div className={`${classes.leftContainer} ${mainClasses.contentWrapper}`}>
                    <div className={classes.headerActions}>
                        <MainTransparentBackButton
                            onClick={() => {
                                const state = location.state;
                                if (state?.from) {
                                    navigate(state?.from, {
                                        state: {
                                            from: `/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${CORPORATE_BACKOFFICE_PATHS.RCA.ROOT}/${CORPORATE_BACKOFFICE_PATHS.RCA.RCA_ACTION_PLANS.ROOT}/${actionPlan.action_plan_id}`,
                                        },
                                    });
                                } else {
                                    navigate(
                                        `/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${CORPORATE_BACKOFFICE_PATHS.RCA.ROOT}/${CORPORATE_BACKOFFICE_PATHS.RCA.RCA_ACTION_PLANS.ROOT}`,
                                    );
                                }
                            }}
                        />
                        <RCAElipsisMenu options={rcaElipsisMenuOptions} />
                    </div>

                    <Breadcrumbs className={classes.ticketNumber} aria-label="breadcrumb">
                        {parentTicketLink}
                        <RCATicketNumber
                            ticketNumber={actionPlan?.display_number || ""}
                            ticketType={IRCATicketTypeEnum.ACTION_PLAN}
                        />
                    </Breadcrumbs>

                    <div className={classes.issueTitle}>
                        <span>{actionPlan.name}</span>
                    </div>
                    <Divider className={classes.divider} />
                    <Box className={mainClasses.contentFormBox}>
                        <InputLabel id="outlined-issueDescription-label">
                            <Typography variant="subtitle2" className={mainClasses.contentFormLabel}>
                                {t("rca.rca_library.rca_detail.action_plans.details")}
                            </Typography>
                        </InputLabel>
                        <TextField
                            disabled
                            fullWidth
                            className={`${mainClasses.contentFormMultitextField} ${mainClasses.contentNoBorderedTextField}`}
                            multiline
                            rows={4}
                            type="text"
                            variant="outlined"
                            value={actionPlan.description}
                        />
                    </Box>
                    <Card className={`${mainClasses.contentCard} ${classes.card}`}>
                        <div className={mainClasses.contentCardHeader}>
                            <div className={mainClasses.contentCardTitle}>
                                <span>{t("rca.action_plans.action_plan_details.tasks.title")}</span>
                            </div>
                            <div className={mainClasses.contentCardActions}></div>
                        </div>
                        <div className={classes.cardContent}>
                            <RCAActionPlanTasks actionPlanId={Number(actionPlanId)!} refetchLogs={refetchHistoryLogs} />
                        </div>
                    </Card>
                    <ActionPlanComments
                        actionPlanId={Number(actionPlanId)}
                        canEdit={RCA_PERMISSIONS?.action_plans?.canEdit || false}
                    />
                    <Card className={`${mainClasses.contentCard} ${classes.card}`}>
                        <div className={mainClasses.contentCardHeader}>
                            <div className={mainClasses.contentCardTitle}>
                                <span>{t("rca.issues.issue_detail.documents.title")}</span>
                            </div>
                            <div className={mainClasses.contentCardActions}>
                                <MainCardButtonAction
                                    disabled={!RCA_PERMISSIONS?.action_plans.canEdit}
                                    title={t("rca.issues.issue_detail.documents.upload_a_file")}
                                    renderIcon={() => <FileUploadOutlinedIcon />}
                                    onClick={() => dropzoneDocumentsRef.current?.open()}
                                />
                            </div>
                        </div>
                        <div className={classes.cardContent}>
                            {!!RCA_PERMISSIONS?.action_plans.canEdit && (
                                <UploadDropZone
                                    onFilesChange={(files) => handleFileChange(files, RCAUploadTypeEnum.DOCUMENT)}
                                    hidePreview
                                    progress={progress}
                                    dropzoneRef={dropzoneDocumentsRef}
                                    showAddedFiles={false}
                                    renderUploadZone={() => <RCAUploadZone uploadType={RCAUploadTypeEnum.DOCUMENT} />}
                                    acceptedFileTypes={documentsFileTypeExtensions}
                                />
                            )}
                            {isUploadingFiles === RCAUploadTypeEnum.DOCUMENT && (
                                <div className={classes.progressWrapper}>
                                    <div>
                                        <span>{t("rca.issues.issue_detail.documents.uploading")}</span>
                                    </div>
                                    <LinearProgress variant="determinate" value={progress} color="info" />
                                </div>
                            )}
                            {!!documentFiles && documentFiles.length > 0 && (
                                <div className={classes.documentFilesWrapper}>
                                    {documentFiles.map((documentFile) => (
                                        <DocumentPreviewer
                                            canDelete={RCA_PERMISSIONS?.action_plans?.canDelete}
                                            document={documentFile}
                                            key={documentFile.action_plan_media_id}
                                            handleDelete={async () =>
                                                await handleDeleteMediaFile(documentFile.action_plan_media_id)
                                            }
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    </Card>
                    <Card className={`${mainClasses.contentCard} ${classes.card}`}>
                        <div className={mainClasses.contentCardHeader}>
                            <div className={mainClasses.contentCardTitle}>
                                <span>{t("rca.issues.issue_detail.media.title")}</span>
                            </div>
                            <div className={mainClasses.contentCardActions}>
                                <MainCardButtonAction
                                    disabled={!RCA_PERMISSIONS?.action_plans.canEdit}
                                    title={t("rca.issues.issue_detail.media.upload_a_photo_or_video")}
                                    renderIcon={() => <FileUploadOutlinedIcon />}
                                    onClick={() => dropzoneMediaRef.current?.open()}
                                />
                            </div>
                        </div>
                        <div className={classes.cardContent}>
                            {!!RCA_PERMISSIONS?.action_plans.canEdit && (
                                <UploadDropZone
                                    dropzoneRef={dropzoneMediaRef}
                                    onFilesChange={(files) => handleFileChange(files, RCAUploadTypeEnum.MEDIA)}
                                    hidePreview
                                    showAddedFiles={false}
                                    renderUploadZone={() => <RCAUploadZone uploadType={RCAUploadTypeEnum.MEDIA} />}
                                    acceptedFileTypes={mediaFileTypeExtensions}
                                />
                            )}
                            {isUploadingFiles === RCAUploadTypeEnum.MEDIA && (
                                <div className={classes.progressWrapper}>
                                    <div>
                                        <span>{t("rca.issues.issue_detail.media.uploading")}</span>
                                    </div>
                                    <LinearProgress variant="determinate" value={progress} color="info" />
                                </div>
                            )}
                            {!!mediaFiles && mediaFiles.length > 0 && (
                                <div className={classes.mediaFilesWrapper}>
                                    {mediaFiles.map((mediaFile) => (
                                        <RCAMediaPreviewer
                                            canDelete={RCA_PERMISSIONS?.action_plans?.canDelete}
                                            handleDelete={async () =>
                                                await handleDeleteMediaFile(mediaFile.action_plan_media_id)
                                            }
                                            mediaFile={mediaFile.media}
                                            key={mediaFile.action_plan_media_id}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    </Card>
                    <RCAActionPlanHistoryLogs logs={actionPlanLogs} loadingLogs={loadingHistoryLogs} />
                </div>

                <div className={classes.rightContainer}>
                    <Formik
                        initialValues={initFormValues}
                        validationSchema={addRCAActionPlanDetailFormValidationSchema}
                        onSubmit={handleSubmit}
                    >
                        {({ values, setFieldValue, dirty, errors, touched }) => {
                            return (
                                <Form className={mainClasses.contentForm}>
                                    <RCAStatusDropdown
                                        disabled={
                                            isSaving ||
                                            !RCA_PERMISSIONS?.action_plans.canEdit ||
                                            values.statusId === RCA_STATUSES.NEEDS_APPROVAL ||
                                            values.statusId === RCA_STATUSES.REJECTED
                                        }
                                        handleChange={(value) => {
                                            setFieldValue("statusId", value);
                                        }}
                                        forActionPlan
                                        excludeStatuses={
                                            values.statusId !== RCA_STATUSES.NEEDS_APPROVAL &&
                                            values.statusId !== RCA_STATUSES.REJECTED
                                                ? [RCA_STATUSES.NEEDS_APPROVAL, RCA_STATUSES.REJECTED]
                                                : []
                                        }
                                        value={values.statusId}
                                    />
                                    <Divider className={classes.formItemDivider} />
                                    <Box className={mainClasses.contentFormBox}>
                                        <InputLabel id="outlined-dueDate-label">
                                            <Typography variant="subtitle2" className={mainClasses.contentFormLabel}>
                                                {t("rca.issues.issue_detail.due_date")}
                                            </Typography>
                                        </InputLabel>

                                        <DatePicker
                                            disablePast
                                            className={`${mainClasses.contentFormDatePicker} ${classes.datePicker}`}
                                            value={dayjs(values.dueDate)}
                                            format={getDateFormatByLanguage()}
                                            disabled={isSaving || !RCA_PERMISSIONS?.action_plans.canEdit}
                                            onChange={(date) => {
                                                setFieldValue("dueDate", date);
                                            }}
                                        />
                                    </Box>
                                    <Divider className={classes.formItemDivider} />
                                    <RCAPriorityDropdown
                                        errors={errors}
                                        touched={touched}
                                        disabled={isSaving || !RCA_PERMISSIONS?.action_plans.canEdit}
                                        handleChange={(value) => {
                                            setFieldValue("priorityId", value);
                                        }}
                                        value={values.priorityId}
                                    />
                                    <Divider className={classes.formItemDivider} />
                                    <Box className={mainClasses.contentFormBox}>
                                        <InputLabel id="outlined-owner-label">
                                            <Typography variant="subtitle2" className={mainClasses.contentFormLabel}>
                                                {t("rca.action_plans.action_plan_details.owner")}
                                            </Typography>
                                        </InputLabel>
                                        <RCAUsersDropdown
                                            fullWidth
                                            disabled={isSaving || !RCA_PERMISSIONS?.action_plans.canEdit}
                                            name="assigneeId"
                                            placeholder={t("rca.action_plans.action_plan_details.select_submitted_by")}
                                            value={values.assigneeId}
                                            onChange={(selected) => {
                                                setFieldValue("assigneeId", selected);
                                            }}
                                            multiple={false}
                                        />
                                    </Box>
                                    <Divider className={classes.formItemDivider} />
                                    <Box className={mainClasses.contentFormBox}>
                                        <InputLabel id="outlined-meetingTypeId-label">
                                            <Typography variant="subtitle2" className={mainClasses.contentFormLabel}>
                                                {t("rca.rca_library.rca_detail.meeting_type")}
                                            </Typography>
                                        </InputLabel>
                                        <RCAMeetingTypesDropdown
                                            fullWidth
                                            multiple={false}
                                            disabled={isSaving || !RCA_PERMISSIONS?.action_plans.canEdit}
                                            value={values.meetingTypeId}
                                            onChange={(value) => {
                                                setFieldValue("meetingTypeId", value);
                                            }}
                                        />
                                    </Box>
                                    <Divider className={classes.formItemDivider} />
                                    <Box className={mainClasses.contentFormBox}>
                                        <InputLabel id="outlined-owner-label">
                                            <Typography variant="subtitle2" className={mainClasses.contentFormLabel}>
                                                {t("rca.action_plans.action_plan_details.submitted_by")}
                                            </Typography>
                                        </InputLabel>
                                        <RCAUsersDropdown
                                            millId={actionPlan?.rca?.mill_id || actionPlan?.issue?.mill_id}
                                            fullWidth
                                            disabled
                                            name="submittedById"
                                            placeholder={t("rca.action_plans.action_plan_details.select_submitted_by")}
                                            value={actionPlan.submitted_by_user_id}
                                            onChange={(selected) => {}}
                                            multiple={false}
                                            className={classes.disabledDropdown}
                                        />
                                    </Box>
                                    <Divider className={classes.formItemDivider} />
                                    {actionPlan.status.status_id === RCA_STATUSES.NEEDS_APPROVAL &&
                                        approvalButtons.map((button) => (
                                            <Button
                                                className={mainClasses.contentButton}
                                                variant="contained"
                                                color={button.approval ? "info" : "error"}
                                                type="button"
                                                disabled={
                                                    isSaving || !RCA_PERMISSIONS?.action_plans.canApproveActionPlans
                                                }
                                                onClick={async () => {
                                                    await handleActionPlanApprovalOrRejection(
                                                        setFieldValue,
                                                        button.approval,
                                                    );
                                                }}
                                            >
                                                {t(
                                                    `rca.action_plans.action_plan_details.${
                                                        button.approval ? "approve" : "reject"
                                                    }`,
                                                )}
                                            </Button>
                                        ))}
                                    {actionPlan.status.status_id === RCA_STATUSES.REJECTED && (
                                        <Button
                                            className={mainClasses.contentButton}
                                            variant="contained"
                                            color="info"
                                            type="button"
                                            disabled={isSaving || !RCA_PERMISSIONS?.action_plans.canEdit}
                                            onClick={async () => {
                                                try {
                                                    await updateActionPlanMutation({
                                                        variables: {
                                                            actionPlanId: actionPlan?.action_plan_id,
                                                            changes: {
                                                                status_id: RCA_STATUSES.NEEDS_APPROVAL,
                                                                resubmitted_for_approval_by: user_id,
                                                            },
                                                        },
                                                    });
                                                    await refetch();
                                                    await refetchHistoryLogs();
                                                    setFieldValue("statusId", RCA_STATUSES.NEEDS_APPROVAL);
                                                    setSnackbarMessage(
                                                        t(
                                                            "rca.action_plans.action_plan_details.resubmit_for_aproval_success",
                                                        ),
                                                    );
                                                } catch {
                                                    return;
                                                }
                                            }}
                                        >
                                            {t("rca.action_plans.action_plan_details.resubmit_for_approval")}
                                        </Button>
                                    )}
                                    <Button
                                        className={mainClasses.contentButton}
                                        variant={
                                            actionPlan.status.status_id === RCA_STATUSES.NEEDS_APPROVAL ||
                                            actionPlan.status.status_id === RCA_STATUSES.REJECTED
                                                ? "outlined"
                                                : "contained"
                                        }
                                        color="info"
                                        type="submit"
                                        disabled={isSaving || !dirty || !RCA_PERMISSIONS?.action_plans.canEdit}
                                    >
                                        {t("rca.issues.issue_detail.save_changes")}
                                    </Button>
                                </Form>
                            );
                        }}
                    </Formik>
                    <Snackbar
                        open={snackbarMessage !== ""}
                        onClose={() => setSnackbarMessage("")}
                        autoHideDuration={6000}
                        anchorOrigin={{
                            vertical: "top",
                            horizontal: "right",
                        }}
                    >
                        <Alert severity="success" sx={{ width: "100%" }}>
                            {snackbarMessage}
                        </Alert>
                    </Snackbar>
                    <Snackbar
                        open={snackbarFailureMessage !== ""}
                        onClose={() => setSnackbarFailureMessage("")}
                        autoHideDuration={6000}
                        anchorOrigin={{
                            vertical: "top",
                            horizontal: "right",
                        }}
                    >
                        <Alert severity="error" sx={{ width: "100%" }}>
                            {snackbarFailureMessage}
                        </Alert>
                    </Snackbar>
                    <RCAConfirmDeleteModal
                        open={openActionPlanDeleteModal}
                        onClose={() => setOpenActionPlanDeleteModal(false)}
                        onDeleteItem={handleDeleteActionPlan}
                        title={t("rca.action_plans.action_plan_delete_modal.title")}
                        warning={t("rca.action_plans.action_plan_delete_modal.warning")}
                        confirm={t("rca.action_plans.action_plan_delete_modal.confirm")}
                    />
                    <RCAEditActionPlanModal
                        open={openEditActionPlanModalOpen}
                        onClose={() => setOpenEditActionPlanModalOpen(false)}
                        onEditActionPlan={async () => {
                            await refetch();
                            await refetchHistoryLogs();
                            setOpenEditActionPlanModalOpen(false);
                            setSnackbarMessage(t("rca.action_plans.action_plan_details.update_success"));
                        }}
                        actionPlan={actionPlanEditDetails}
                        onError={() => {
                            setOpenEditActionPlanModalOpen(false);
                            setSnackbarFailureMessage(t("rca.action_plans.action_plan_details.update_failed"));
                        }}
                    />
                    <MainModal
                        isOpen={isFileUploadModalOpen}
                        title={t("upload_files_description_modal.title")}
                        onClose={() => setIsFileUploadModalOpen(false)}
                        onOkButtonClick={() => {
                            setIsFileUploadModalOpen(false);
                            handleFileUpload();
                        }}
                    >
                        <TextField
                            className={mainClasses.contentFormTextFieldModal}
                            type="text"
                            variant="outlined"
                            placeholder={t("upload_files_description_modal.text_field_placeholder")}
                            value={fileDescription}
                            onChange={(e) => setFileDescription(e.target.value)}
                        />
                    </MainModal>
                </div>
            </div>
        </div>
    );
};

export default RCAActionPlanDetail;
